import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '@/api/config';
import toast from 'react-hot-toast';

export interface CampaignStats {
    total_sent: number;
    total_opened: number;
    total_clicked: number;
    total_replied: number;
    total_bounced: number;
    open_rate: number;
    click_rate: number;
    reply_rate: number;
    bounce_rate: number;
}

export interface TrackerRow {
    id: number;
    recipient_email: string;
    status: string;
    is_opened: boolean;
    is_clicked: boolean;
    is_replied: boolean;
    is_bounced: boolean;
    opened_at: string | null;
    clicked_at: string | null;
    replied_at: string | null;
    bounced_at: string | null;
    click_count: number;
    clicked_links?: Record<string, number>;
    email_id?: string;
}

export interface ReplyDetail {
    from: string;
    to: string;
    subject: string;
    date: string;
    html_content: string;
}

const STATUS_PRIORITY: Record<string, number> = {
    Replied: 1, REPLIED: 1,
    Opened: 2, OPENED: 2,
    Clicked: 2.5, CLICKED: 2.5,
    Sent: 3, SENT: 3,
    Bounced: 4, BOUNCED: 4,
};

function sortTrackers(trackers: TrackerRow[]): TrackerRow[] {
    return [...trackers].sort((a, b) => {
        const aS = a.is_bounced ? 'Bounced' : a.is_replied ? 'Replied' : a.is_clicked ? 'Clicked' : a.is_opened ? 'Opened' : 'Sent';
        const bS = b.is_bounced ? 'Bounced' : b.is_replied ? 'Replied' : b.is_clicked ? 'Clicked' : b.is_opened ? 'Opened' : 'Sent';
        return (STATUS_PRIORITY[aS] || 5) - (STATUS_PRIORITY[bS] || 5);
    });
}

export function useCampaignDetails(campaignUuid: string | null) {
    const queryClient = useQueryClient();

    const { data, isLoading, refetch } = useQuery(
        ['campaignAnalytics', campaignUuid],
        async () => {
            const res = await api.get(`/api/campaign/${campaignUuid}/analytics`);
            return res.data;
        },
        {
            enabled: !!campaignUuid,
            refetchInterval: 15000,
            staleTime: 5000,
        }
    );

    const refreshStatsMutation = useMutation(
        () => api.post(`/api/campaign/${campaignUuid}/refresh-stats`),
        {
            onSuccess: () => {
                toast.success('Stats refreshed!');
                queryClient.invalidateQueries(['campaignAnalytics', campaignUuid]);
            },
            onError: () => { toast.error('Failed to refresh stats'); },
        }
    );

    const deleteMutation = useMutation(
        () => api.post(`/campaign/${campaignUuid}/delete`),
        {
            onSuccess: () => {
                toast.success('Campaign deleted');
                queryClient.invalidateQueries('campaigns');
            },
            onError: () => { toast.error('Failed to delete campaign'); },
        }
    );

    const restoreMutation = useMutation(
        () => api.post(`/campaign/${campaignUuid}/restore`),
        {
            onSuccess: () => {
                toast.success('Campaign restored');
                queryClient.invalidateQueries('campaigns');
            },
            onError: () => { toast.error('Failed to restore campaign'); },
        }
    );

    const resendEmailMutation = useMutation(
        (payload: { tracker_id: number; recipient_email: string }) =>
            api.post(`/campaign/${campaignUuid}/resend`, payload),
        {
            onSuccess: () => {
                toast.success('Email resent successfully!');
                queryClient.invalidateQueries(['campaignAnalytics', campaignUuid]);
            },
            onError: () => { toast.error('Failed to resend email'); },
        }
    );

    const sendReplyMutation = useMutation(
        (payload: { tracker_id: number; to: string; subject: string; content: string }) =>
            api.post(`/campaign/${campaignUuid}/tracker/${payload.tracker_id}/send-reply`, {
                to: payload.to,
                subject: payload.subject,
                content: payload.content,
            }),
        {
            onSuccess: () => {
                toast.success('Reply sent successfully!');
                queryClient.invalidateQueries(['campaignAnalytics', campaignUuid]);
            },
            onError: () => { toast.error('Failed to send reply'); },
        }
    );

    const generateReplyMutation = useMutation(
        (payload: { tracker_id: number }) =>
            api.post(`/api/campaign/${campaignUuid}/tracker/${payload.tracker_id}/generate-reply`),
        {
            onError: (err: any) => {
                toast.error(err?.response?.data?.error || 'Failed to generate AI reply');
            },
        }
    );

    const generateFollowupMutation = useMutation(
        (payload: { tracker_id: number }) =>
            api.post(`/api/campaign/${campaignUuid}/tracker/${payload.tracker_id}/generate-followup`),
        {
            onError: (err: any) => {
                toast.error(err?.response?.data?.error || 'Failed to generate follow-up');
            },
        }
    );

    const checkReplyMutation = useMutation(
        (payload: { tracker_id: number; email_id?: string }) => {
            const endpoint = payload.email_id
                ? `/email/${payload.email_id}/check-reply`
                : `/tracker/${payload.tracker_id}/check-reply`;
            return api.post(endpoint);
        },
        {
            onSuccess: () => {
                toast.success('Reply check complete!');
                queryClient.invalidateQueries(['campaignAnalytics', campaignUuid]);
            },
            onError: () => { toast.error('Failed to check reply'); },
        }
    );

    const fetchTrackerReply = async (trackerId: number): Promise<{ success: boolean; message: ReplyDetail | null }> => {
        const res = await api.get(`/api/campaign/${campaignUuid}/tracker/${trackerId}/reply`);
        return res.data;
    };

    return {
        campaign: data?.campaign ?? null,
        stats: (data?.stats ?? null) as CampaignStats | null,
        trackers: sortTrackers(data?.trackers ?? []) as TrackerRow[],
        chartData: {
            dates: (data?.dates ?? []) as string[],
            opens: (data?.opens_data ?? []) as number[],
            clicks: (data?.clicks_data ?? []) as number[],
        },
        deviceData: {
            labels: (data?.device_labels ?? []) as string[],
            data: (data?.device_data ?? []) as number[],
        },
        isLoading,
        refetchDetail: refetch,
        refreshStatsMutation,
        deleteMutation,
        restoreMutation,
        resendEmailMutation,
        sendReplyMutation,
        generateReplyMutation,
        generateFollowupMutation,
        checkReplyMutation,
        fetchTrackerReply,
    };
}
